import { useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { SuccessToast } from 'components/success-toast';
import {
  ReportFormModal,
  type ReportFormModalProps,
} from './report-form-modal';
import type { ReportType } from 'pages/bugs-features/types';
import type { CurrentOncall } from 'types/api-responses';
import {
  isReportFormOpenAtom,
  reportFormInitialTypeAtom,
  reportFormErrorAtom,
  reportSubmittedAtom,
} from './atoms';

/**
 * Renders the one report form for the whole app, mounted at the shell so the
 * header button works from any page.
 *
 * Kept separate from the modal itself so the modal stays a plain controlled
 * component — usable in tests and stories without the atoms.
 *
 * The on-call lookup is handed in as a hook and only called while the form is open, so a
 * page with the form closed makes no request and needs no query client — the shell is
 * rendered bare in a good many page tests.
 *
 * @param {object} props
 * @param {() => CurrentOncall} props.useOncall Reads this week's on-call engineer.
 * @return {JSX.Element | null} The report form modal, or null while it is closed.
 */
export function ReportFormHost({
  useOncall,
}: {
  useOncall: () => CurrentOncall;
}) {
  const [isOpen, setIsOpen] = useAtom(isReportFormOpenAtom);
  const initialType = useAtomValue(reportFormInitialTypeAtom);
  const crash = useAtomValue(reportFormErrorAtom);
  const setSubmitted = useSetAtom(reportSubmittedAtom);
  const setCrash = useSetAtom(reportFormErrorAtom);

  // What was just filed, so the toast can name it. Held here rather than in the modal
  // because the modal unmounts the instant it closes — which is exactly when the
  // confirmation needs to appear.
  const [submittedType, setSubmittedType] = useState<ReportType | null>(null);

  const toast = submittedType && (
    <SuccessToast
      message={
        submittedType === 'bug' ? 'Bug reported' : 'Feature request submitted'
      }
      subtitle="We will follow up. You are watching it, so you will hear about any change."
      onClose={() => {
        setSubmittedType(null);
        return true;
      }}
    />
  );

  // Unmounted while closed rather than left mounted with open={false}. Carbon renders a
  // modal's `role="dialog"` container either way, so keeping it mounted parks a permanent
  // dialog in the accessibility tree of every page — and, because the shell renders this
  // above the router, it is the *first* dialog any `getAllByRole('dialog')` finds.
  //
  // The toast is returned even while the form is closed, because closing the form is
  // precisely when it shows.
  if (!isOpen) return toast;

  // The listing still only needs the refetch — bugs and feature requests share one table.
  // The kind is used for the toast's wording, which is the one place the difference shows.
  return (
    <>
      {toast}
      <ReportFormModalWithOncall
        useOncall={useOncall}
        open={isOpen}
        initialReportType={initialType}
        crash={crash}
        // Cleared on close as well as on submit: the next person to open the form from the
        // header is not reporting this crash, and a stale trace attached to an unrelated
        // report is worse than none.
        onClose={() => {
          setCrash(null);
          setIsOpen(false);
        }}
        onSubmitted={(reportType) => {
          setCrash(null);
          setSubmittedType(reportType);
          setSubmitted((n) => n + 1);
        }}
      />
    </>
  );
}

/**
 * The modal with this week's on-call engineer filled in. Its own component so the lookup
 * starts when the form mounts — that is, on each open — and not before.
 *
 * @param {object} props
 * @param {() => CurrentOncall} props.useOncall Reads this week's on-call engineer.
 * @return {JSX.Element} The report form modal.
 */
function ReportFormModalWithOncall({
  useOncall,
  ...modalProps
}: Omit<ReportFormModalProps, 'oncall'> & {
  useOncall: () => CurrentOncall;
}) {
  const oncall = useOncall();

  return <ReportFormModal {...modalProps} oncall={oncall} />;
}
